export const dataPlaneTypeDefs = `

scalar AWSDateTime

type AgentPostPassReport {
  agentId: String!
  markdownText: String!
  s3Key: String!
  taskDocumentId: String!
}

type Aggregation {
  _avg: Float!
  _max: Float!
  _min: Float!
  _p1: Float!
  _p5: Float!
  _p25: Float!
  _p50: Float!
  _p75: Float!
  _p95: Float!
  _p99: Float!
  _stdDev: Float!
  _sum: Float!
}

type Contact {
  agentPostPassReports: [AgentPostPassReport!]
  dataplaneReport: DataplaneReport
  id: String!
  images: [PostPassImage!]
  laminarFlowPlots: [LaminarFlowPlot!]
  laminarFlowReport: LaminarFlowReport
  tracerouteDetails: TracerouteDetails
  visualization: ContactVisualization
}

input ContactByIdInput {
  id: String!
}

type ContactVisualization {
  dataplaneSvgUrlDark: String
  dataplaneSvgUrlLight: String
  s3Key: String
}

type CursorPart {
  key: String!
  value: String!
}

input CursorPartInput {
  key: String!
  value: String!
}

type DDXStatistic {
  avg: Float
  median: Float
  p50: Float
  p75: Float
  p90: Float
  p95: Float
  p99: Float
  stdDev: Float
}

input DDXStatisticFilterInput {
  _and: [DDXStatisticFilterInput!]
  _exists: Boolean
  _not: DDXStatisticFilterInput
  _or: [DDXStatisticFilterInput!]
  avg: FloatFilterInput
  median: FloatFilterInput
  p50: FloatFilterInput
  p75: FloatFilterInput
  p90: FloatFilterInput
  p95: FloatFilterInput
  p99: FloatFilterInput
  stdDev: FloatFilterInput
}

type DDXStreamMetric {
  dataLatency: DDXStatistic
  dataPacketsReceived: Int
  dataRate: DDXStatistic
  streamDirection: String!
  streamId: String!
  streamName: String!
  wanLatency: DDXStatistic
  wanOverhead: DDXStatistic
  wanPacketsDropped: Int
  wanPacketsDuplicated: Int
  wanPacketsLost: Int
  wanPacketsReceived: Int
  wanPacketsReordered: Int
  wanPacketsRepaired: Int
  wanRate: DDXStatistic
  wanRtt: DDXStatistic
}

input DDXStreamMetricFilterInput {
  _and: [DDXStreamMetricFilterInput!]
  _exists: Boolean
  _not: DDXStreamMetricFilterInput
  _or: [DDXStreamMetricFilterInput!]
  dataLatency: DDXStatisticFilterInput
  dataPacketsReceived: IntegerFilterInput
  dataRate: DDXStatisticFilterInput
  streamDirection: StringFilterInput
  streamId: StringFilterInput
  streamName: StringFilterInput
  wanLatency: DDXStatisticFilterInput
  wanOverhead: DDXStatisticFilterInput
  wanPacketsDropped: IntegerFilterInput
  wanPacketsDuplicated: IntegerFilterInput
  wanPacketsLost: IntegerFilterInput
  wanPacketsReceived: IntegerFilterInput
  wanPacketsReordered: IntegerFilterInput
  wanPacketsRepaired: IntegerFilterInput
  wanRate: DDXStatisticFilterInput
  wanRtt: DDXStatisticFilterInput
}

type DataPlaneDDXMetric {
  """The account of the CPS account for the job"""
  accountId: String!

  """Unique identifier for the contact"""
  contactId: String!
  customerNamePart: String

  """The end of the contact time"""
  endTime: String!

  """Unique indentifier for the DDX job"""
  jobId: String!

  """The end of post-pass"""
  postPassEndTime: String!

  """The start time of pre-pass"""
  prePassStartTime: String!

  """The CPS region of the the account running the job"""
  region: String!

  """The start of the contact time"""
  startTime: String!
  streamMetrics: [DDXStreamMetric!]!
}

input DataPlaneDDXMetricFilterInput {
  _and: [DataPlaneDDXMetricFilterInput!]
  _exists: Boolean
  _not: DataPlaneDDXMetricFilterInput
  _or: [DataPlaneDDXMetricFilterInput!]
  accountId: StringFilterInput
  contactId: StringFilterInput
  customerNamePart: StringFilterInput
  endTime: StringFilterInput
  jobId: StringFilterInput
  postPassEndTime: StringFilterInput
  prePassStartTime: StringFilterInput
  region: StringFilterInput
  startTime: StringFilterInput
  streamMetrics: DDXStreamMetricFilterInput
}

input DataPlaneDDXMetricSortInput {
  accountId: SortOrder
  contactId: SortOrder
  customerNamePart: SortOrder
  endTime: SortOrder
  jobId: SortOrder
  postPassEndTime: SortOrder
  prePassStartTime: SortOrder
  region: SortOrder
  startTime: SortOrder
}

type DataPlaneLFMetric {
  agentInformation: LaminarFlowAgentMetadata
  agent_id: String!
  contactStatus: String
  contact_id: String!
  customerId: String
  customerNamePart: String
  dataflow_id: String!
  destinationRegion: String
  id: String!
  lfDecryptStageMetricsV2: LfDecryptStageMetricsV2
  lfEgressStageMetricsV2: LfEgressStageMetricsV2
  lfEncryptStageMetricsV2: LfEncryptStageMetricsV2
  lfIngressStageMetricsV2: LfIngressStageMetricsV2
  lfStripeDecodeStageMetricsV2: LfStripeDecodeStageMetricsV2
  lfStripeDecodeStageMetricsV3: LfStripeDecodeStageMetricsV3
  lfStripeEncodeStageMetricsV2: LfStripeEncodeStageMetricsV2
  lfTimeDecodeStageMetricsV2: LfTimeDecodeStageMetricsV2
  lfTimeDecodeStageMetricsV3: LfTimeDecodeStageMetricsV3
  lfTimeEncodeStageMetricsV2: LfTimeEncodeStageMetricsV2
  lfTracerouteStageMetricsV2: [LfTracerouteStageMetricsV2!]
  lfWanRxStageMetricsV2: LfWanRxStageMetricsV2
  lfWanTxStageMetricsV2: LfWanTxStageMetricsV2

  """
  The band type classification used for LFCG parameter selection (e.g. NARROWBAND, WIDEBAND)
  """
  lfcgBandType: String

  """The LFCG calculation strategy used for this dataflow (e.g. V1, V2)"""
  lfcgCalculationStrategy: String
  missionProfileId: String
  rpm_version: String!
  sourceRegion: String
  task_id: String!
  timeStampEnd: String
  timeStampStart: String
}

type DataPlaneLFMetricAggregation {
  lfDecryptStageMetricsV2: LfDecryptStageMetricsV2Aggregation!
  lfEgressStageMetricsV2: LfEgressStageMetricsV2Aggregation!
  lfEncryptStageMetricsV2: LfEncryptStageMetricsV2Aggregation!
  lfIngressStageMetricsV2: LfIngressStageMetricsV2Aggregation!
  lfStripeDecodeStageMetricsV2: LfStripeDecodeStageMetricsV2Aggregation!
  lfStripeDecodeStageMetricsV3: LfStripeDecodeStageMetricsV3Aggregation!
  lfStripeEncodeStageMetricsV2: LfStripeEncodeStageMetricsV2Aggregation!
  lfTimeDecodeStageMetricsV2: LfTimeDecodeStageMetricsV2Aggregation!
  lfTimeDecodeStageMetricsV3: LfTimeDecodeStageMetricsV3Aggregation!
  lfTimeEncodeStageMetricsV2: LfTimeEncodeStageMetricsV2Aggregation!
  lfWanRxStageMetricsV2: LfWanRxStageMetricsV2Aggregation!
  lfWanTxStageMetricsV2: LfWanTxStageMetricsV2Aggregation!
}

input DataPlaneLFMetricFilterInput {
  _and: [DataPlaneLFMetricFilterInput!]
  _exists: Boolean
  _not: DataPlaneLFMetricFilterInput
  _or: [DataPlaneLFMetricFilterInput!]
  agentInformation: LaminarFlowAgentMetadataFilterInput
  agent_id: StringFilterInput
  contactStatus: StringFilterInput
  contact_id: StringFilterInput
  customerId: StringFilterInput
  customerNamePart: StringFilterInput
  dataflow_id: StringFilterInput
  destinationRegion: StringFilterInput
  id: StringFilterInput
  lfDecryptStageMetricsV2: LfDecryptStageMetricsV2FilterInput
  lfEgressStageMetricsV2: LfEgressStageMetricsV2FilterInput
  lfEncryptStageMetricsV2: LfEncryptStageMetricsV2FilterInput
  lfIngressStageMetricsV2: LfIngressStageMetricsV2FilterInput
  lfStripeDecodeStageMetricsV2: LfStripeDecodeStageMetricsV2FilterInput
  lfStripeDecodeStageMetricsV3: LfStripeDecodeStageMetricsV3FilterInput
  lfStripeEncodeStageMetricsV2: LfStripeEncodeStageMetricsV2FilterInput
  lfTimeDecodeStageMetricsV2: LfTimeDecodeStageMetricsV2FilterInput
  lfTimeDecodeStageMetricsV3: LfTimeDecodeStageMetricsV3FilterInput
  lfTimeEncodeStageMetricsV2: LfTimeEncodeStageMetricsV2FilterInput
  lfTracerouteStageMetricsV2: LfTracerouteStageMetricsV2FilterInput
  lfWanRxStageMetricsV2: LfWanRxStageMetricsV2FilterInput
  lfWanTxStageMetricsV2: LfWanTxStageMetricsV2FilterInput
  lfcgBandType: StringFilterInput
  lfcgCalculationStrategy: StringFilterInput
  missionProfileId: StringFilterInput
  rpm_version: StringFilterInput
  sourceRegion: StringFilterInput
  task_id: StringFilterInput
  timeStampEnd: StringFilterInput
  timeStampStart: StringFilterInput
}

input DataPlaneLFMetricSortInput {
  agentInformation: LaminarFlowAgentMetadataSortInput
  agent_id: SortOrder
  contactStatus: SortOrder
  contact_id: SortOrder
  customerId: SortOrder
  customerNamePart: SortOrder
  dataflow_id: SortOrder
  destinationRegion: SortOrder
  id: SortOrder
  lfDecryptStageMetricsV2: LfDecryptStageMetricsV2SortInput
  lfEgressStageMetricsV2: LfEgressStageMetricsV2SortInput
  lfEncryptStageMetricsV2: LfEncryptStageMetricsV2SortInput
  lfIngressStageMetricsV2: LfIngressStageMetricsV2SortInput
  lfStripeDecodeStageMetricsV2: LfStripeDecodeStageMetricsV2SortInput
  lfStripeDecodeStageMetricsV3: LfStripeDecodeStageMetricsV3SortInput
  lfStripeEncodeStageMetricsV2: LfStripeEncodeStageMetricsV2SortInput
  lfTimeDecodeStageMetricsV2: LfTimeDecodeStageMetricsV2SortInput
  lfTimeDecodeStageMetricsV3: LfTimeDecodeStageMetricsV3SortInput
  lfTimeEncodeStageMetricsV2: LfTimeEncodeStageMetricsV2SortInput
  lfWanRxStageMetricsV2: LfWanRxStageMetricsV2SortInput
  lfWanTxStageMetricsV2: LfWanTxStageMetricsV2SortInput
  lfcgBandType: SortOrder
  lfcgCalculationStrategy: SortOrder
  missionProfileId: SortOrder
  rpm_version: SortOrder
  sourceRegion: SortOrder
  task_id: SortOrder
  timeStampEnd: SortOrder
  timeStampStart: SortOrder
}

type DataplaneReport {
  htmlText: String!
}

input EchoInput {
  input: String!
}

type EchoOutput {
  output: String!
}

input FloatFilterInput {
  _eq: Float
  _exists: Boolean
  _gt: Float
  _gte: Float
  _in: [Float!]
  _lt: Float
  _lte: Float
  _neq: Float
  _nin: [Float!]
}

input GetDataPlaneDDXMetricInput {
  jobId: String
}

input GetDataPlaneLFMetricInput {
  id: String
}

input IntegerFilterInput {
  _eq: Int
  _exists: Boolean
  _gt: Int
  _gte: Int
  _in: [Int!]
  _lt: Int
  _lte: Int
  _neq: Int
  _nin: [Int!]
}

type LaminarFlowAgentMetadata {
  agentAccountId: String
  agentDestinationInstanceId: String
  agentInstanceId: String
  agentInstanceType: String
}

input LaminarFlowAgentMetadataFilterInput {
  _and: [LaminarFlowAgentMetadataFilterInput!]
  _exists: Boolean
  _not: LaminarFlowAgentMetadataFilterInput
  _or: [LaminarFlowAgentMetadataFilterInput!]
  agentAccountId: StringFilterInput
  agentDestinationInstanceId: StringFilterInput
  agentInstanceId: StringFilterInput
  agentInstanceType: StringFilterInput
}

input LaminarFlowAgentMetadataSortInput {
  agentAccountId: SortOrder
  agentDestinationInstanceId: SortOrder
  agentInstanceId: SortOrder
  agentInstanceType: SortOrder
}

input LaminarFlowLossReportInput {
  endTime: AWSDateTime
  excludeInternalContacts: Boolean
  startTime: AWSDateTime
}

type LaminarFlowLossReportOutput {
  markdownText: String!
}

type LaminarFlowMetric {
  avg: Float
  max: Float
  median: Float
  min: Float
  p50: Float
  p75: Float
  p90: Float
  p95: Float
  p99: Float
  stdDev: Float
  total: Float
}

type LaminarFlowMetricAggregation {
  avg: Aggregation!
  max: Aggregation!
  median: Aggregation!
  min: Aggregation!
  p50: Aggregation!
  p75: Aggregation!
  p90: Aggregation!
  p95: Aggregation!
  p99: Aggregation!
  stdDev: Aggregation!
  total: Aggregation!
}

input LaminarFlowMetricFilterInput {
  _and: [LaminarFlowMetricFilterInput!]
  _exists: Boolean
  _not: LaminarFlowMetricFilterInput
  _or: [LaminarFlowMetricFilterInput!]
  avg: FloatFilterInput
  max: FloatFilterInput
  median: FloatFilterInput
  min: FloatFilterInput
  p50: FloatFilterInput
  p75: FloatFilterInput
  p90: FloatFilterInput
  p95: FloatFilterInput
  p99: FloatFilterInput
  stdDev: FloatFilterInput
  total: FloatFilterInput
}

input LaminarFlowMetricSortInput {
  avg: SortOrder
  max: SortOrder
  median: SortOrder
  min: SortOrder
  p50: SortOrder
  p75: SortOrder
  p90: SortOrder
  p95: SortOrder
  p99: SortOrder
  stdDev: SortOrder
  total: SortOrder
}

type LaminarFlowPlot {
  agentId: String
  dataflowId: String
  imageBase64: String
  plotName: String
  presignedGetUrl: String
  s3Key: String!
}

type LaminarFlowReport {
  presignedGetUrl: String
  s3Key: String!
}

type LfDecryptStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__decryption_error_rate: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
  metric__total_decryption_errors: LaminarFlowMetric
}

type LfDecryptStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__decryption_error_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
  metric__total_decryption_errors: LaminarFlowMetricAggregation!
}

input LfDecryptStageMetricsV2FilterInput {
  _and: [LfDecryptStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfDecryptStageMetricsV2FilterInput
  _or: [LfDecryptStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__decryption_error_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
  metric__total_decryption_errors: LaminarFlowMetricFilterInput
}

input LfDecryptStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__decryption_error_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
  metric__total_decryption_errors: LaminarFlowMetricSortInput
}

type LfEgressStageMetricsV2 {
  id: String
  metric__average_bytes_per_syscall: LaminarFlowMetric
  metric__average_datagrams_per_syscall: LaminarFlowMetric
  metric__average_max_depth_per_syscall: LaminarFlowMetric
  metric__average_min_depth_per_syscall: LaminarFlowMetric
  metric__average_width_per_syscall: LaminarFlowMetric
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__connection_error_rate: LaminarFlowMetric
  metric__connection_refused_error_rate: LaminarFlowMetric
  metric__initial_input_sequence_count: LaminarFlowMetric
  metric__input_bit_rate: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__last_input_sequence_count: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
  metric__network_unreachable_error_rate: LaminarFlowMetric
  metric__output_bit_rate: LaminarFlowMetric
  metric__overall_timestamp_delta_sec_max: LaminarFlowMetric
  metric__overall_timestamp_delta_sec_min: LaminarFlowMetric
  metric__packets_lost_sequence_count: LaminarFlowMetric
  metric__packets_out_of_order_sequence_count: LaminarFlowMetric
  metric__socket_overflow_rate: LaminarFlowMetric
  metric__timestamp_delta_sec_max: LaminarFlowMetric
  metric__timestamp_delta_sec_min: LaminarFlowMetric
  metric__total_input_bytes: LaminarFlowMetric
  metric__total_output_bytes: LaminarFlowMetric
  metric__total_packets_lost_sequence_count: LaminarFlowMetric
  metric__total_packets_out_of_order_sequence_count: LaminarFlowMetric
  metric__total_retransmitted_messages: LaminarFlowMetric
  metric__total_socket_overflows: LaminarFlowMetric
}

type LfEgressStageMetricsV2Aggregation {
  metric__average_bytes_per_syscall: LaminarFlowMetricAggregation!
  metric__average_datagrams_per_syscall: LaminarFlowMetricAggregation!
  metric__average_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__average_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__average_width_per_syscall: LaminarFlowMetricAggregation!
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__connection_error_rate: LaminarFlowMetricAggregation!
  metric__connection_refused_error_rate: LaminarFlowMetricAggregation!
  metric__initial_input_sequence_count: LaminarFlowMetricAggregation!
  metric__input_bit_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__last_input_sequence_count: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
  metric__network_unreachable_error_rate: LaminarFlowMetricAggregation!
  metric__output_bit_rate: LaminarFlowMetricAggregation!
  metric__overall_timestamp_delta_sec_max: LaminarFlowMetricAggregation!
  metric__overall_timestamp_delta_sec_min: LaminarFlowMetricAggregation!
  metric__packets_lost_sequence_count: LaminarFlowMetricAggregation!
  metric__packets_out_of_order_sequence_count: LaminarFlowMetricAggregation!
  metric__socket_overflow_rate: LaminarFlowMetricAggregation!
  metric__timestamp_delta_sec_max: LaminarFlowMetricAggregation!
  metric__timestamp_delta_sec_min: LaminarFlowMetricAggregation!
  metric__total_input_bytes: LaminarFlowMetricAggregation!
  metric__total_output_bytes: LaminarFlowMetricAggregation!
  metric__total_packets_lost_sequence_count: LaminarFlowMetricAggregation!
  metric__total_packets_out_of_order_sequence_count: LaminarFlowMetricAggregation!
  metric__total_retransmitted_messages: LaminarFlowMetricAggregation!
  metric__total_socket_overflows: LaminarFlowMetricAggregation!
}

input LfEgressStageMetricsV2FilterInput {
  _and: [LfEgressStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfEgressStageMetricsV2FilterInput
  _or: [LfEgressStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__average_bytes_per_syscall: LaminarFlowMetricFilterInput
  metric__average_datagrams_per_syscall: LaminarFlowMetricFilterInput
  metric__average_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__average_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__average_width_per_syscall: LaminarFlowMetricFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__connection_error_rate: LaminarFlowMetricFilterInput
  metric__connection_refused_error_rate: LaminarFlowMetricFilterInput
  metric__initial_input_sequence_count: LaminarFlowMetricFilterInput
  metric__input_bit_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__last_input_sequence_count: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
  metric__network_unreachable_error_rate: LaminarFlowMetricFilterInput
  metric__output_bit_rate: LaminarFlowMetricFilterInput
  metric__overall_timestamp_delta_sec_max: LaminarFlowMetricFilterInput
  metric__overall_timestamp_delta_sec_min: LaminarFlowMetricFilterInput
  metric__packets_lost_sequence_count: LaminarFlowMetricFilterInput
  metric__packets_out_of_order_sequence_count: LaminarFlowMetricFilterInput
  metric__socket_overflow_rate: LaminarFlowMetricFilterInput
  metric__timestamp_delta_sec_max: LaminarFlowMetricFilterInput
  metric__timestamp_delta_sec_min: LaminarFlowMetricFilterInput
  metric__total_input_bytes: LaminarFlowMetricFilterInput
  metric__total_output_bytes: LaminarFlowMetricFilterInput
  metric__total_packets_lost_sequence_count: LaminarFlowMetricFilterInput
  metric__total_packets_out_of_order_sequence_count: LaminarFlowMetricFilterInput
  metric__total_retransmitted_messages: LaminarFlowMetricFilterInput
  metric__total_socket_overflows: LaminarFlowMetricFilterInput
}

input LfEgressStageMetricsV2SortInput {
  id: SortOrder
  metric__average_bytes_per_syscall: LaminarFlowMetricSortInput
  metric__average_datagrams_per_syscall: LaminarFlowMetricSortInput
  metric__average_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__average_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__average_width_per_syscall: LaminarFlowMetricSortInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__connection_error_rate: LaminarFlowMetricSortInput
  metric__connection_refused_error_rate: LaminarFlowMetricSortInput
  metric__initial_input_sequence_count: LaminarFlowMetricSortInput
  metric__input_bit_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__last_input_sequence_count: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
  metric__network_unreachable_error_rate: LaminarFlowMetricSortInput
  metric__output_bit_rate: LaminarFlowMetricSortInput
  metric__overall_timestamp_delta_sec_max: LaminarFlowMetricSortInput
  metric__overall_timestamp_delta_sec_min: LaminarFlowMetricSortInput
  metric__packets_lost_sequence_count: LaminarFlowMetricSortInput
  metric__packets_out_of_order_sequence_count: LaminarFlowMetricSortInput
  metric__socket_overflow_rate: LaminarFlowMetricSortInput
  metric__timestamp_delta_sec_max: LaminarFlowMetricSortInput
  metric__timestamp_delta_sec_min: LaminarFlowMetricSortInput
  metric__total_input_bytes: LaminarFlowMetricSortInput
  metric__total_output_bytes: LaminarFlowMetricSortInput
  metric__total_packets_lost_sequence_count: LaminarFlowMetricSortInput
  metric__total_packets_out_of_order_sequence_count: LaminarFlowMetricSortInput
  metric__total_retransmitted_messages: LaminarFlowMetricSortInput
  metric__total_socket_overflows: LaminarFlowMetricSortInput
}

type LfEncryptStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__encryption_error_rate: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
  metric__total_encryption_errors: LaminarFlowMetric
}

type LfEncryptStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__encryption_error_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
  metric__total_encryption_errors: LaminarFlowMetricAggregation!
}

input LfEncryptStageMetricsV2FilterInput {
  _and: [LfEncryptStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfEncryptStageMetricsV2FilterInput
  _or: [LfEncryptStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__encryption_error_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
  metric__total_encryption_errors: LaminarFlowMetricFilterInput
}

input LfEncryptStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__encryption_error_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
  metric__total_encryption_errors: LaminarFlowMetricSortInput
}

type LfIngressStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__input_bit_rate: LaminarFlowMetric
  metric__input_drop_rate: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__last_output_sequence_count: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
  metric__output_bit_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
  metric__socket_back_off_rate: LaminarFlowMetric
  metric__socket_error_rate: LaminarFlowMetric
  metric__total_early_input_bytes: LaminarFlowMetric
  metric__total_input_bytes: LaminarFlowMetric
  metric__total_input_drops: LaminarFlowMetric
  metric__total_late_input_bytes: LaminarFlowMetric
  metric__total_output_bytes: LaminarFlowMetric
  metric__total_socket_back_offs: LaminarFlowMetric
  metric__total_socket_errors: LaminarFlowMetric
  metric__total_timely_input_bytes: LaminarFlowMetric
}

type LfIngressStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__input_bit_rate: LaminarFlowMetricAggregation!
  metric__input_drop_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__last_output_sequence_count: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_bit_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
  metric__socket_back_off_rate: LaminarFlowMetricAggregation!
  metric__socket_error_rate: LaminarFlowMetricAggregation!
  metric__total_early_input_bytes: LaminarFlowMetricAggregation!
  metric__total_input_bytes: LaminarFlowMetricAggregation!
  metric__total_input_drops: LaminarFlowMetricAggregation!
  metric__total_late_input_bytes: LaminarFlowMetricAggregation!
  metric__total_output_bytes: LaminarFlowMetricAggregation!
  metric__total_socket_back_offs: LaminarFlowMetricAggregation!
  metric__total_socket_errors: LaminarFlowMetricAggregation!
  metric__total_timely_input_bytes: LaminarFlowMetricAggregation!
}

input LfIngressStageMetricsV2FilterInput {
  _and: [LfIngressStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfIngressStageMetricsV2FilterInput
  _or: [LfIngressStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__input_bit_rate: LaminarFlowMetricFilterInput
  metric__input_drop_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__last_output_sequence_count: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_bit_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
  metric__socket_back_off_rate: LaminarFlowMetricFilterInput
  metric__socket_error_rate: LaminarFlowMetricFilterInput
  metric__total_early_input_bytes: LaminarFlowMetricFilterInput
  metric__total_input_bytes: LaminarFlowMetricFilterInput
  metric__total_input_drops: LaminarFlowMetricFilterInput
  metric__total_late_input_bytes: LaminarFlowMetricFilterInput
  metric__total_output_bytes: LaminarFlowMetricFilterInput
  metric__total_socket_back_offs: LaminarFlowMetricFilterInput
  metric__total_socket_errors: LaminarFlowMetricFilterInput
  metric__total_timely_input_bytes: LaminarFlowMetricFilterInput
}

input LfIngressStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__input_bit_rate: LaminarFlowMetricSortInput
  metric__input_drop_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__last_output_sequence_count: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
  metric__output_bit_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
  metric__socket_back_off_rate: LaminarFlowMetricSortInput
  metric__socket_error_rate: LaminarFlowMetricSortInput
  metric__total_early_input_bytes: LaminarFlowMetricSortInput
  metric__total_input_bytes: LaminarFlowMetricSortInput
  metric__total_input_drops: LaminarFlowMetricSortInput
  metric__total_late_input_bytes: LaminarFlowMetricSortInput
  metric__total_output_bytes: LaminarFlowMetricSortInput
  metric__total_socket_back_offs: LaminarFlowMetricSortInput
  metric__total_socket_errors: LaminarFlowMetricSortInput
  metric__total_timely_input_bytes: LaminarFlowMetricSortInput
}

type LfStripeDecodeStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing_history: [Float!]
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetric
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetric
  metric__head_block_set_number: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__last_processed_stripe_index: LaminarFlowMetric
  metric__output_block_error_rate: LaminarFlowMetric
  metric__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
  metric__tail_block_set_number: LaminarFlowMetric
}

type LfStripeDecodeStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricAggregation!
  metric__head_block_set_number: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__last_processed_stripe_index: LaminarFlowMetricAggregation!
  metric__output_block_error_rate: LaminarFlowMetricAggregation!
  metric__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
  metric__tail_block_set_number: LaminarFlowMetricAggregation!
}

input LfStripeDecodeStageMetricsV2FilterInput {
  _and: [LfStripeDecodeStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfStripeDecodeStageMetricsV2FilterInput
  _or: [LfStripeDecodeStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing_history: FloatFilterInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricFilterInput
  metric__head_block_set_number: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__last_processed_stripe_index: LaminarFlowMetricFilterInput
  metric__output_block_error_rate: LaminarFlowMetricFilterInput
  metric__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
  metric__tail_block_set_number: LaminarFlowMetricFilterInput
}

input LfStripeDecodeStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricSortInput
  metric__head_block_set_number: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__last_processed_stripe_index: LaminarFlowMetricSortInput
  metric__output_block_error_rate: LaminarFlowMetricSortInput
  metric__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
  metric__tail_block_set_number: LaminarFlowMetricSortInput
}

type LfStripeDecodeStageMetricsV3 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing_history: [Float!]
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetric
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetric
  metric__head_block_set_number: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__last_processed_stripe_index: LaminarFlowMetric
  metric__output_block_error_rate: LaminarFlowMetric
  metric__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
  metric__tail_block_set_number: LaminarFlowMetric
}

type LfStripeDecodeStageMetricsV3Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricAggregation!
  metric__head_block_set_number: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__last_processed_stripe_index: LaminarFlowMetricAggregation!
  metric__output_block_error_rate: LaminarFlowMetricAggregation!
  metric__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
  metric__tail_block_set_number: LaminarFlowMetricAggregation!
}

input LfStripeDecodeStageMetricsV3FilterInput {
  _and: [LfStripeDecodeStageMetricsV3FilterInput!]
  _exists: Boolean
  _not: LfStripeDecodeStageMetricsV3FilterInput
  _or: [LfStripeDecodeStageMetricsV3FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing_history: FloatFilterInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricFilterInput
  metric__head_block_set_number: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__last_processed_stripe_index: LaminarFlowMetricFilterInput
  metric__output_block_error_rate: LaminarFlowMetricFilterInput
  metric__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
  metric__tail_block_set_number: LaminarFlowMetricFilterInput
}

input LfStripeDecodeStageMetricsV3SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricSortInput
  metric__head_block_set_number: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__last_processed_stripe_index: LaminarFlowMetricSortInput
  metric__output_block_error_rate: LaminarFlowMetricSortInput
  metric__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
  metric__tail_block_set_number: LaminarFlowMetricSortInput
}

type LfStripeEncodeStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
}

type LfStripeEncodeStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
}

input LfStripeEncodeStageMetricsV2FilterInput {
  _and: [LfStripeEncodeStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfStripeEncodeStageMetricsV2FilterInput
  _or: [LfStripeEncodeStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
}

input LfStripeEncodeStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
}

type LfTimeDecodeStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing_history: [Float!]
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetric
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__output_block_error_rate: LaminarFlowMetric
  metric__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
}

type LfTimeDecodeStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__output_block_error_rate: LaminarFlowMetricAggregation!
  metric__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
}

input LfTimeDecodeStageMetricsV2FilterInput {
  _and: [LfTimeDecodeStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfTimeDecodeStageMetricsV2FilterInput
  _or: [LfTimeDecodeStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing_history: FloatFilterInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__output_block_error_rate: LaminarFlowMetricFilterInput
  metric__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
}

input LfTimeDecodeStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__output_block_error_rate: LaminarFlowMetricSortInput
  metric__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
}

type LfTimeDecodeStageMetricsV3 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_missing_history: [Float!]
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetric
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetric
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__output_block_error_rate: LaminarFlowMetric
  metric__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
}

type LfTimeDecodeStageMetricsV3Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricAggregation!
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__output_block_error_rate: LaminarFlowMetricAggregation!
  metric__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
}

input LfTimeDecodeStageMetricsV3FilterInput {
  _and: [LfTimeDecodeStageMetricsV3FilterInput!]
  _exists: Boolean
  _not: LfTimeDecodeStageMetricsV3FilterInput
  _or: [LfTimeDecodeStageMetricsV3FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_missing_history: FloatFilterInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricFilterInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__output_block_error_rate: LaminarFlowMetricFilterInput
  metric__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
}

input LfTimeDecodeStageMetricsV3SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_error_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_block_rate: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_duplicated: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_missing: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_orphaned: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_out_of_order: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_recovered: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_stale: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_blocks_unrecoverable: LaminarFlowMetricSortInput
  metric__decoding_input_metrics__input_virtual_fill_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__output_block_error_rate: LaminarFlowMetricSortInput
  metric__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
}

type LfTimeEncodeStageMetricsV2 {
  id: String
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
}

type LfTimeEncodeStageMetricsV2Aggregation {
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
}

input LfTimeEncodeStageMetricsV2FilterInput {
  _and: [LfTimeEncodeStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfTimeEncodeStageMetricsV2FilterInput
  _or: [LfTimeEncodeStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
}

input LfTimeEncodeStageMetricsV2SortInput {
  id: SortOrder
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
}

type LfTracerouteStageMetricsV2 {
  id: String
  metric__dest: String
  metric__hop_count: Int
  metric__hops__addr: [String!]
  metric__hops__est_rtt: [Float!]
  metric__hops__ttl: [Float!]
  metric__id: String
  metric__source: String
  metric__status: String
  timestamp: String
}

input LfTracerouteStageMetricsV2FilterInput {
  _and: [LfTracerouteStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfTracerouteStageMetricsV2FilterInput
  _or: [LfTracerouteStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__dest: StringFilterInput
  metric__hop_count: IntegerFilterInput
  metric__hops__addr: StringFilterInput
  metric__hops__est_rtt: FloatFilterInput
  metric__hops__ttl: FloatFilterInput
  metric__id: StringFilterInput
  metric__source: StringFilterInput
  metric__status: StringFilterInput
  timestamp: StringFilterInput
}

type LfWanRxStageMetricsV2 {
  id: String
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetric
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetric
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetric
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetric
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__selected_lessor: String
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__received_heartbeat_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__selected_lessee: String
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_heartbeat_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_lease_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetric
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetric
}

type LfWanRxStageMetricsV2Aggregation {
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetricAggregation!
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetricAggregation!
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetricAggregation!
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetricAggregation!
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__received_heartbeat_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_heartbeat_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_lease_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricAggregation!
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricAggregation!
}

input LfWanRxStageMetricsV2FilterInput {
  _and: [LfWanRxStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfWanRxStageMetricsV2FilterInput
  _or: [LfWanRxStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetricFilterInput
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetricFilterInput
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetricFilterInput
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetricFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__selected_lessor: StringFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__received_heartbeat_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__selected_lessee: StringFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_heartbeat_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_lease_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricFilterInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricFilterInput
}

input LfWanRxStageMetricsV2SortInput {
  id: SortOrder
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetricSortInput
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetricSortInput
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetricSortInput
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetricSortInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__selected_lessor: SortOrder
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__received_heartbeat_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__selected_lessee: SortOrder
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_heartbeat_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__transmitted_lease_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__output_queue_overflow_rate: LaminarFlowMetricSortInput
  metric__output_queue_metrics__total_output_queue_overflows: LaminarFlowMetricSortInput
}

type LfWanTxStageMetricsV2 {
  id: String
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetric
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetric
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetric
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetric
  metric__common_stage_metrics__tick_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_heartbeat_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_lease_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__selected_lessor: String
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__transmitted_heartbeat_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__selected_lessee: String
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetric
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetric
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetric
  metric__dropped_message_count: LaminarFlowMetric
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetric
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetric
  metric__message_metrics__input_message_rate: LaminarFlowMetric
  metric__message_metrics__output_message_rate: LaminarFlowMetric
}

type LfWanTxStageMetricsV2Aggregation {
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetricAggregation!
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetricAggregation!
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetricAggregation!
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetricAggregation!
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_heartbeat_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_lease_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__transmitted_heartbeat_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricAggregation!
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricAggregation!
  metric__dropped_message_count: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricAggregation!
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricAggregation!
  metric__message_metrics__input_message_rate: LaminarFlowMetricAggregation!
  metric__message_metrics__output_message_rate: LaminarFlowMetricAggregation!
}

input LfWanTxStageMetricsV2FilterInput {
  _and: [LfWanTxStageMetricsV2FilterInput!]
  _exists: Boolean
  _not: LfWanTxStageMetricsV2FilterInput
  _or: [LfWanTxStageMetricsV2FilterInput!]
  id: StringFilterInput
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetricFilterInput
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetricFilterInput
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetricFilterInput
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetricFilterInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_heartbeat_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_lease_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__selected_lessor: StringFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__transmitted_heartbeat_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__selected_lessee: StringFilterInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricFilterInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricFilterInput
  metric__dropped_message_count: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricFilterInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricFilterInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricFilterInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricFilterInput
}

input LfWanTxStageMetricsV2SortInput {
  id: SortOrder
  metric__bytes_metrics__input_bit_rate: LaminarFlowMetricSortInput
  metric__bytes_metrics__output_bit_rate: LaminarFlowMetricSortInput
  metric__bytes_metrics__total_input_bytes: LaminarFlowMetricSortInput
  metric__bytes_metrics__total_output_bytes: LaminarFlowMetricSortInput
  metric__common_stage_metrics__tick_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_heartbeat_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__received_lease_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__selected_lessor: SortOrder
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessee_metrics__transmitted_heartbeat_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_handlers_metrics__transmit_lessor_metrics__selected_lessee: SortOrder
  metric__control_message_manager_metrics__control_message_metrics__decryption_error_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__decryption_success_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__input_control_message_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__output_control_message_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_input_control_message_rate: LaminarFlowMetricSortInput
  metric__control_message_manager_metrics__control_message_metrics__unsupported_output_control_message_rate: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__control_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__recvmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_datagram_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_max_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_min_depth_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_segment_count_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__mean_width_per_syscall: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__eagain: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__econnrefused: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__ehostunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__enetunreach: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__socket_error_counts__other: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_error_count: LaminarFlowMetricSortInput
  metric__data_plane_socket_metrics__sendmmsg_metrics__syscall_rate: LaminarFlowMetricSortInput
  metric__dropped_message_count: LaminarFlowMetricSortInput
  metric__input_queue_metrics__input_queue_underflow_rate: LaminarFlowMetricSortInput
  metric__input_queue_metrics__total_input_queue_underflows: LaminarFlowMetricSortInput
  metric__message_metrics__input_message_rate: LaminarFlowMetricSortInput
  metric__message_metrics__output_message_rate: LaminarFlowMetricSortInput
}

input ListDataPlaneDDXMetricInput {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [DataPlaneDDXMetricSortInput!]
  where: DataPlaneDDXMetricFilterInput
}

type ListDataPlaneDDXMetricOutput {
  pageInfo: PageInfo!
  results: [DataPlaneDDXMetric!]!
}

input ListDataPlaneLFMetricInput {
  cursor: [CursorPartInput!]
  limit: Int = 10
  sort: [DataPlaneLFMetricSortInput!]
  where: DataPlaneLFMetricFilterInput
}

type ListDataPlaneLFMetricOutput {
  aggregations: DataPlaneLFMetricAggregation!
  pageInfo: PageInfo!
  results: [DataPlaneLFMetric!]!
}

type PageInfo {
  hasNextPage: Boolean!
  lastCursor: [CursorPart!]
  total: Total!
}

type PostPassImage {
  dataflowId: String
  imageBase64: String
  jobId: String
  presignedGetUrl: String
  s3Key: String!
}

type PresignedDeviceDetail {
  url: String
}

type PresignedS3Metric {
  agentId: String
  dataflowId: String
  taskId: String
  url: String
}

type Query {
  echo(cazJustification: String, input: EchoInput!): EchoOutput
  getContactById(cazJustification: String, input: ContactByIdInput!): Contact
  getDataPlaneDDXMetric(cazJustification: String, input: GetDataPlaneDDXMetricInput!): DataPlaneDDXMetric
  getDataPlaneLFMetric(cazJustification: String, input: GetDataPlaneLFMetricInput!): DataPlaneLFMetric
  getLaminarFlowLossReport(cazJustification: String, input: LaminarFlowLossReportInput!): LaminarFlowLossReportOutput
  listDataPlaneDDXMetric(cazJustification: String, input: ListDataPlaneDDXMetricInput!): ListDataPlaneDDXMetricOutput
  listDataPlaneLFMetric(cazJustification: String, input: ListDataPlaneLFMetricInput!): ListDataPlaneLFMetricOutput
}

enum Relation {
  eq
  gte
}

enum SortOrder {
  asc
  desc
}

input StringFilterInput {
  _eq: String
  _exists: Boolean
  _gt: String
  _gte: String
  _in: [String!]
  _lt: String
  _lte: String
  _neq: String
  _nin: [String!]
  _startsWith: String
}

type Total {
  relation: Relation
  value: Int
}

type TracerouteDetails {
  deviceDetails: [PresignedDeviceDetail!]
  egressStageLossPresignedS3Url: [PresignedS3Metric!]
  rawTracerouteMetricsPresignedS3Url: [PresignedS3Metric!]
  timeDecodeLoss: [PresignedS3Metric!]
}
`;
